import { providers } from '../ai/registry.js';
import { assetUrl } from '../assets/asset-urls.js';
import { newsVideoPrompt } from '../config/prompts/news.js';
import { VIDEO_POLL_MS } from '../config/app-config.js';
import { sleep } from '../util/sleep.js';
import { customizations, startStatus } from '../state/app-state.js';
import citiesData from '../data/cities.json';
import type { Slot } from '../types/manifest.js';
import type { NewsStory } from '../ai/provider.js';

export interface NewsHandlers {
  onNewsReady: (slot: 'news.us' | 'news.local', url: string, story: NewsStory) => void;
}

export function renderNewsPanel(host: HTMLElement, prospectGetter: () => string | null, h: NewsHandlers): void {
  const cities = (citiesData as { cities: string[] }).cities;
  host.innerHTML = `
    <div class="panel">
      <h2>News</h2>
      <div class="row">
        <button id="np-us" class="button">US News</button>
        <button id="np-local" class="button">Local News</button>
        <select id="np-city">${cities.map((c) => `<option>${c}</option>`).join('')}</select>
        <div id="np-status"></div>
      </div>
    </div>`;
  const us = host.querySelector<HTMLButtonElement>('#np-us')!;
  const local = host.querySelector<HTMLButtonElement>('#np-local')!;
  const city = host.querySelector<HTMLSelectElement>('#np-city')!;
  const status = host.querySelector<HTMLDivElement>('#np-status')!;

  async function go(scope: 'us' | 'local') {
    const prospect = prospectGetter();
    if (!prospect) { status.textContent = 'Initialize a prospect first.'; return; }
    const slot: Slot = scope === 'us' ? 'news.us' : 'news.local';
    const button = scope === 'us' ? us : local;
    button.disabled = true;
    const oldLabel = button.textContent;
    button.textContent = 'Generating…';
    status.textContent = '';
    const done = startStatus(`Generating ${scope === 'us' ? 'US' : 'Local'} news clip`);
    try {
      const story = scope === 'us'
        ? await providers.text.fetchTopUSStory()
        : await providers.text.fetchTopLocalStory(city.value);

      const slotId: 'news.us' | 'news.local' = scope === 'us' ? 'news.us' : 'news.local';
      const cur = customizations.get();
      const customSlot = cur.slots[slotId];

      let geminiPromptUsed: string;
      let klingPromptUsed: string;
      if (customSlot?.klingPrompt) {
        // Operator has set a direct Kling prompt — bypass the Gemini craft step.
        geminiPromptUsed = customSlot.geminiPrompt ?? '';
        klingPromptUsed = `${customSlot.klingPrompt}\n\nContext: ${story.headline}. ${story.summary}`;
      } else {
        // Default flow — Gemini crafts the Kling prompt.
        try {
          klingPromptUsed = await providers.text.craftNewsVideoPrompt(scope, story);
        } catch {
          // Fallback to the simple template if the Gemini-crafted version fails.
          klingPromptUsed = newsVideoPrompt(scope, story);
        }
        geminiPromptUsed = `[Auto-generated by Gemini news-video-prompt for "${story.headline}"]`;
      }

      // Persist the prompts actually used so the modal can display them.
      const updated: typeof cur = {
        ...cur,
        slots: {
          ...cur.slots,
          [slotId]: { ...customSlot, geminiPrompt: geminiPromptUsed, klingPrompt: klingPromptUsed },
        },
      };
      customizations.set(updated);
      if (prospect) await providers.text.saveCustomizations(prospect, updated);

      const { requestId } = await providers.video.start({
        prospect, slot, prompt: klingPromptUsed, durationSec: 5,
      });
      let assetKey: string | undefined;
      while (true) {
        const r = await providers.video.poll(requestId);
        if (r.status === 'done' && r.assetKey) { assetKey = r.assetKey; break; }
        if (r.status === 'error') throw new Error(r.errorMessage ?? 'video failed');
        await sleep(VIDEO_POLL_MS);
      }
      h.onNewsReady(slot as 'news.us' | 'news.local', assetUrl(prospect, assetKey), story);
      status.textContent = story.headline;
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : 'News failed';
    } finally {
      done();
      button.disabled = false;
      button.textContent = oldLabel;
    }
  }

  us.addEventListener('click', () => go('us'));
  local.addEventListener('click', () => go('local'));
}
